import { useId, useMemo, useRef, useState, type PointerEvent } from 'react'
import { fmtDateHeure } from '../../api.js'
import { domaineY, fmtQuand, fmtValeur, troncons, type Point } from './modele.js'

/**
 * UNE COURBE, FAITE MAIN EN SVG (21/10/2026). Pas de bibliothèque.
 *
 *   · un trou est un trou : la ligne se coupe et la plage sans donnée est
 *     hachurée, avec « pas de donnée » au survol — jamais un zéro tracé ;
 *   · une seule échelle verticale ; cyan = donnée qui circule (un rôle) ;
 *   · bande min–max discrète pour une jauge agrégée, marches pour un état,
 *     colonnes pour un compteur par intervalle ;
 *   · réticule + infobulle au survol ; les étiquettes vivent en HTML (le SVG
 *     s'étire sans déformer le texte) ; traits non déformés ;
 *   · `marques` : des plages surlignées en ambre derrière la courbe (les
 *     passages sur batterie d'un onduleur, 27/10/2026), avec leur légende.
 */

const L = 1000

export interface PropsCourbe {
  points: Point[]
  seuilS: number
  unite: string | null
  agg: 'gauge' | 'delta' | 'state'
  debut: number
  fin: number
  hauteur?: number
  libelle: string
  /** Plages [début, fin] en ms, surlignées ; `marqueLibelle` les nomme au survol. */
  marques?: [number, number][]
  marqueLibelle?: string
}

export function Courbe({ points, seuilS, unite, agg, debut, fin, hauteur = 120, libelle, marques, marqueLibelle = 'Plage marquée' }: PropsCourbe) {
  const motif = useId().replace(/:/g, '')
  const zone = useRef<HTMLDivElement>(null)
  const [survol, setSurvol] = useState<{ x: number; p: Point } | null>(null)

  const calc = useMemo(() => {
    const reels = points.filter((p): p is [number, number, number | null, number | null] => p[1] !== null && Number.isFinite(p[1]))
      .slice().sort((a, b) => a[0] - b[0])
    const valeurs = reels.flatMap((p) => [p[1], ...(agg === 'gauge' ? [p[2], p[3]] : [])]).filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
    const [lo, hi] = domaineY(valeurs, unite)
    const span = Math.max(1, fin - debut)
    const x = (t: number) => ((t - debut) / span) * L
    const H = hauteur
    const y = (v: number) => H - 2 - ((v - lo) / (hi - lo)) * (H - 6)
    const { troncons: tr, trous } = troncons(points, seuilS, debut, fin)
    let ecart = Infinity
    for (let i = 1; i < reels.length; i++) ecart = Math.min(ecart, reels[i]![0] - reels[i - 1]![0])
    const largeurCol = Number.isFinite(ecart) ? Math.max(1.5, Math.min(24, (ecart / span) * L * 0.7)) : 4
    const parT = new Map(reels.map((p) => [p[0], p]))
    return { reels, parT, lo, hi, x, y, H, tr, trous, largeurCol }
  }, [points, seuilS, unite, agg, debut, fin, hauteur])

  const { reels, parT, lo, hi, x, y, H, tr, trous, largeurCol } = calc
  const ligne = (seg: [number, number][]) => {
    if (agg === 'state') {
      return seg.map(([t, v], i) => {
        const px = x(t).toFixed(1), py = y(v).toFixed(1)
        if (i === 0) return `M${px},${py}`
        return `H${px}V${py}`
      }).join('')
    }
    return seg.map(([t, v], i) => `${i === 0 ? 'M' : 'L'}${x(t).toFixed(1)},${y(v).toFixed(1)}`).join('')
  }
  const bande = (seg: [number, number][]) => {
    const pts = seg.map(([t]) => parT.get(t)).filter((p): p is NonNullable<typeof p> => !!p && p[2] !== null && p[3] !== null)
    if (pts.length < 2) return null
    const haut = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p[0]).toFixed(1)},${y(p[3]!).toFixed(1)}`).join('')
    const bas = pts.slice().reverse().map((p) => `L${x(p[0]).toFixed(1)},${y(p[2]!).toFixed(1)}`).join('')
    return `${haut}${bas}Z`
  }

  const surPointeur = (e: PointerEvent<HTMLDivElement>) => {
    const r = zone.current?.getBoundingClientRect()
    if (!r || reels.length === 0) return
    const frac = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width))
    const t = debut + frac * (fin - debut)
    let a = 0, b = reels.length - 1
    while (b - a > 1) { const m = (a + b) >> 1; if (reels[m]![0] < t) a = m; else b = m }
    const p = Math.abs(reels[a]![0] - t) <= Math.abs(reels[b]![0] - t) ? reels[a]! : reels[b]!
    // Au-delà du seuil de trou, le survol tombe dans le vide : on ne prête pas une valeur voisine.
    if (Math.abs(p[0] - t) > Math.max(1, seuilS) * 1000) { setSurvol(null); return }
    setSurvol({ x: (x(p[0]) / L) * 100, p })
  }

  const dernier = reels[reels.length - 1]
  const resume = reels.length === 0 ? `${libelle} : aucune donnée sur la période.`
    : `${libelle} : dernière valeur ${fmtValeur(dernier?.[1], unite)} à ${fmtQuand(dernier ? new Date(dernier[0]).toISOString() : null)}.`

  return (
    <div className="sv-fiche-svg">
      <div className="sv-fiche-svg__zone" ref={zone} onPointerMove={surPointeur} onPointerLeave={() => setSurvol(null)} role="img" aria-label={resume}>
        <span className="sv-fiche-svg__y sv-fiche-svg__y--haut">{fmtValeur(hi, unite)}</span>
        <span className="sv-fiche-svg__y sv-fiche-svg__y--bas">{fmtValeur(lo, unite)}</span>
        <svg viewBox={`0 0 ${L} ${H}`} preserveAspectRatio="none" className="sv-fiche-svg__svg" style={{ height: H }} aria-hidden="true">
          <defs>
            <pattern id={`h${motif}`} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="8" className="sv-fiche-svg__hachure" />
            </pattern>
          </defs>
          {(marques ?? []).filter(([a, b]) => b > debut && a < fin).map(([a, b], i) => {
            const xa = x(Math.max(a, debut)), xb = x(Math.min(b, fin))
            return (
              <rect key={`m${i}`} x={xa} y="0" width={Math.max(2, xb - xa)} height={H} className="sv-fiche-svg__marque">
                <title>{marqueLibelle} : de {fmtQuand(new Date(a).toISOString())} à {fmtQuand(new Date(b).toISOString())}</title>
              </rect>
            )
          })}
          <line x1="0" x2={L} y1={y(hi)} y2={y(hi)} className="sv-fiche-svg__grille" />
          {lo <= 0 && hi >= 0 ? <line x1="0" x2={L} y1={y(0)} y2={y(0)} className="sv-fiche-svg__base" /> : null}
          {trous.map(([a, b], i) => (
            <rect key={`t${i}`} x={x(a)} y="0" width={Math.max(1, x(b) - x(a))} height={H} fill={`url(#h${motif})`} className="sv-fiche-svg__trou">
              <title>Pas de donnée de {fmtQuand(new Date(a).toISOString())} à {fmtQuand(new Date(b).toISOString())}</title>
            </rect>
          ))}
          {agg === 'gauge' ? tr.map((seg, i) => {
            const d = bande(seg)
            return d ? <path key={`b${i}`} d={d} className="sv-fiche-svg__bande" /> : null
          }) : null}
          {agg === 'delta'
            ? reels.map((p) => (
              <rect key={p[0]} x={x(p[0]) - largeurCol / 2} width={largeurCol} y={Math.min(y(p[1]), y(Math.max(lo, 0)))}
                    height={Math.max(p[1] === 0 ? 0 : 1, Math.abs(y(Math.max(lo, 0)) - y(p[1])))} className="sv-fiche-svg__col" />
            ))
            : tr.map((seg, i) => seg.length === 1
              ? <circle key={`p${i}`} cx={x(seg[0]![0])} cy={y(seg[0]![1])} r="2.5" className="sv-fiche-svg__point" />
              : <path key={`l${i}`} d={ligne(seg)} className="sv-fiche-svg__ligne" />)}
        </svg>
        {survol ? (
          <>
            <span className="sv-fiche-svg__reticule" style={{ left: `${survol.x}%` }} aria-hidden="true" />
            <span className={`sv-fiche-svg__bulle${survol.x > 60 ? ' sv-fiche-svg__bulle--gauche' : ''}`} style={{ left: `${survol.x}%` }} role="status">
              <strong>{fmtValeur(survol.p[1], unite)}</strong>
              {agg === 'gauge' && survol.p[2] !== null && survol.p[3] !== null && survol.p[2] !== survol.p[3]
                ? <span>min {fmtValeur(survol.p[2], unite)} · max {fmtValeur(survol.p[3], unite)}</span> : null}
              <span className="sv-fiche-svg__heure">{fmtDateHeure(new Date(survol.p[0]).toISOString())}</span>
            </span>
          </>
        ) : null}
        {reels.length === 0 ? <span className="sv-fiche-svg__rien">Aucune donnée sur la période</span> : null}
      </div>
      <div className="sv-fiche-svg__x" aria-hidden="true">
        <span>{fmtQuand(new Date(debut).toISOString())}</span>
        <span>{fmtQuand(new Date(fin).toISOString())}</span>
      </div>
    </div>
  )
}
